import { AlertTriangle, Info } from 'lucide-react';
import clsx from 'clsx';
import type { ValidationWarning } from '../../hooks/useWarnings';

interface WarningBadgeProps {
  warnings: ValidationWarning[];
  onClick?: () => void;
  className?: string;
}

/**
 * Badge che mostra il numero di warnings attivi
 *
 * Features:
 * - Contatore warnings
 * - Tooltip con summary
 * - Click per aprire pannello warnings
 * - Colori diversi per severity (warning: amber, info: blue)
 */
export function WarningBadge({ warnings, onClick, className }: WarningBadgeProps) {
  if (warnings.length === 0) return null;

  // Conta per severity
  const warningCount = warnings.filter(w => w.severity === 'warning').length;
  const infoCount = warnings.filter(w => w.severity === 'info').length;

  // Determina colore badge basato sulla severity più alta
  const hasWarnings = warningCount > 0;
  const badgeColor = hasWarnings ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-blue-100 text-blue-700 border-blue-300';

  // Tooltip summary
  const tooltipText = [
    warningCount > 0 ? `${warningCount} avvertiment${warningCount > 1 ? 'i' : 'o'}` : null,
    infoCount > 0 ? `${infoCount} info` : null,
  ].filter(Boolean).join(', ');

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full border text-xs font-medium',
        'hover:shadow-sm transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-offset-1',
        hasWarnings ? 'focus:ring-amber-500' : 'focus:ring-blue-500',
        badgeColor,
        className
      )}
      title={tooltipText}
      aria-label={`${warnings.length} avvisi rilevati. Clicca per visualizzare.`}
    >
      {hasWarnings ? (
        <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
      ) : (
        <Info className="w-3.5 h-3.5" aria-hidden="true" />
      )}
      <span>{warnings.length}</span>
    </button>
  );
}
